import { Link } from '@tanstack/react-router'
import { ArrowRight, Banknote } from 'lucide-react'

import { ErrorSurface } from '@/components/error-surface'
import { MoneyDisplay } from '@/components/money-display'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { today } from '@/features/reports/constants'
import { useCollectionsRegister } from '@/features/reports/queries'
import { ReportHeaderNotice } from '@/features/reports/report-header-notice'
import { cn } from '@/lib/utils'

import { useOperatorBranchId } from './operator-branch'

/**
 * What has been collected today, against the collections register's own totals rather than a sum
 * this tile takes over the rows itself — the same `totalCollected` the register screen foots its
 * table with, computed over the whole filtered range regardless of how many rows are fetched. A
 * `pageSize` of one is enough: nothing here reads an individual row.
 *
 * `fromDate`/`toDate` are both the browser's date, exactly as `CollectionsRegisterSection` defaults
 * its own filter — a ceiling for "today", not a business date standing in for one.
 */
export function CollectionsTodayTile({ className }: { className?: string }) {
  const branchId = useOperatorBranchId()
  const date = today()
  const query = useCollectionsRegister({ fromDate: date, toDate: date, branchId, pageSize: 1 }, true)

  return (
    <Card className={cn('flex flex-col', className)}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Banknote className="size-4 text-muted" aria-hidden />
          Collections today
        </CardTitle>
      </CardHeader>

      <CardContent className="flex flex-1 flex-col gap-3">
        {query.isPending ? (
          <Skeleton className="h-24 w-full" />
        ) : query.isError ? (
          <ErrorSurface
            error={query.error}
            onRetry={() => void query.refetch()}
            title="Today's collections could not be read"
          />
        ) : (
          <>
            <ReportHeaderNotice header={query.data.header} />

            <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
              <div className="min-w-0">
                <dt className="text-2xs font-semibold tracking-wide text-muted uppercase">Collected</dt>
                <dd className="mt-0.5">
                  <MoneyDisplay
                    value={query.data.totalCollected.minor}
                    currency={query.data.totalCollected.currency}
                    emphasis="strong"
                    className="text-left"
                  />
                </dd>
              </div>

              <div className="min-w-0">
                <dt className="text-2xs font-semibold tracking-wide text-muted uppercase">
                  Repayments
                </dt>
                <dd className="numeric mt-0.5 text-sm font-medium tabular-nums text-heading">
                  {query.data.paging.totalRows}
                </dd>
              </div>
            </dl>

            {query.data.unbalancedRowCount > 0 ? (
              <Badge tone="warning" className="self-start">
                {query.data.unbalancedRowCount} row
                {query.data.unbalancedRowCount === 1 ? '' : 's'} don&apos;t allocate to the amount
                collected
              </Badge>
            ) : null}
          </>
        )}

        <Button asChild variant="secondary" size="sm" className="self-start">
          <Link to="/reports/collections">
            View collections register
            <ArrowRight />
          </Link>
        </Button>
      </CardContent>
    </Card>
  )
}
